'use client';

import React, { useState, useMemo } from 'react';
import { Search, Download, Mail, MessageCircle, Globe, ChevronUp, ChevronDown, X, ShoppingBag, Phone, Clock, Bot, Star, Filter,  } from 'lucide-react';
import StatusBadge from '@/components/ui/StatusBadge';
import { toast } from 'sonner';

type LeadStatus = 'hot' | 'warm' | 'cold' | 'buyer' | 'new' | 'contacted';
type LeadSource = 'email' | 'whatsapp' | 'organic';

interface Lead {
  id: string;
  name: string;
  contact: string;
  phone?: string;
  source: LeadSource;
  status: LeadStatus;
  score: number;
  bot: string;
  campaign: string;
  lastActivity: string;
  joinedDate: string;
  funnelStep: number;
  hasPurchased: boolean;
  dossierCount: number;
  notes: string;
}

// Backend integration point: replace with real leads API call
const allLeads: Lead[] = [
  {
    id: 'lead-001',
    name: 'María García',
    contact: 'maria.garcia@gmail.com',
    phone: '+34 612 345 678',
    source: 'email',
    status: 'hot',
    score: 87,
    bot: 'Bot Email Funnel',
    campaign: 'Campaña Email Bienvenida',
    lastActivity: 'hace 3 min',
    joinedDate: '20/07/2026',
    funnelStep: 4,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Interesada en dossier París. Abrió 4 de 5 emails.',
  },
  {
    id: 'lead-002',
    name: 'Carlos López',
    contact: 'carlos.lopez@hotmail.com',
    phone: '+34 677 234 567',
    source: 'whatsapp',
    status: 'buyer',
    score: 95,
    bot: 'Bot WhatsApp Outreach',
    campaign: 'WhatsApp Dossier Viaje — Aportación €0.50',
    lastActivity: 'hace 1 hora',
    joinedDate: '19/07/2026',
    funnelStep: 5,
    hasPurchased: true,
    dossierCount: 2,
    notes: 'Aportó para dossier Roma y Barcelona. Potencial colaborador recurrente.',
  },
  {
    id: 'lead-003',
    name: 'Ana Martínez',
    contact: 'ana.mtz@outlook.com',
    phone: '+34 698 123 456',
    source: 'email',
    status: 'warm',
    score: 62,
    bot: 'Bot Email Funnel',
    campaign: 'Campaña Email Bienvenida',
    lastActivity: 'hace 25 min',
    joinedDate: '19/07/2026',
    funnelStep: 3,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Abrió email #3. No ha hecho clic en el CTA todavía.',
  },
  {
    id: 'lead-004',
    name: 'Pedro Sánchez',
    contact: '+34 698 765 432',
    source: 'whatsapp',
    status: 'new',
    score: 41,
    bot: 'Bot WhatsApp Outreach',
    campaign: 'WhatsApp Dossier Viaje — Aportación €0.50',
    lastActivity: 'hace 1 hora',
    joinedDate: '19/07/2026',
    funnelStep: 2,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Nuevo lead. Pendiente de primer contacto automatizado.',
  },
  {
    id: 'lead-005',
    name: 'Laura Fernández',
    contact: 'l.fernandez@yahoo.es',
    phone: '+34 644 555 321',
    source: 'organic',
    status: 'hot',
    score: 79,
    bot: 'Bot Retargeting IA',
    campaign: 'Retargeting Compradores IA',
    lastActivity: 'hace 45 min',
    joinedDate: '18/07/2026',
    funnelStep: 4,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Visitó la página de dossiers 3 veces. Alta intención de colaborar.',
  },
  {
    id: 'lead-006',
    name: 'Javier Ruiz',
    contact: 'javier.ruiz@gmail.com',
    source: 'email',
    status: 'cold',
    score: 23,
    bot: 'Bot Email Funnel',
    campaign: 'Campaña Email Bienvenida',
    lastActivity: 'hace 3 días',
    joinedDate: '17/07/2026',
    funnelStep: 2,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Abrió solo el primer email. No ha respondido desde entonces.',
  },
  {
    id: 'lead-007',
    name: 'Sofía Torres',
    contact: '+34 677 234 567',
    phone: '+34 677 234 567',
    source: 'whatsapp',
    status: 'buyer',
    score: 91,
    bot: 'Bot WhatsApp Outreach',
    campaign: 'WhatsApp Dossier Viaje — Aportación €0.50',
    lastActivity: 'hace 2 horas',
    joinedDate: '18/07/2026',
    funnelStep: 5,
    hasPurchased: true,
    dossierCount: 3,
    notes: 'Aportó para 3 dossiers. Colaboradora fidelizada. Candidata a pack premium.',
  },
  {
    id: 'lead-008',
    name: 'Miguel Herrera',
    contact: 'miguel.h@hotmail.com',
    source: 'organic',
    status: 'warm',
    score: 55,
    bot: 'Bot Retargeting IA',
    campaign: 'Retargeting Compradores IA',
    lastActivity: 'hace 4 horas',
    joinedDate: '18/07/2026',
    funnelStep: 3,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Interesado en comparador de vuelos. Interés en dossier completo pendiente.',
  },
  {
    id: 'lead-009',
    name: 'Elena Moreno',
    contact: 'elena.moreno@gmail.com',
    phone: '+34 655 432 109',
    source: 'email',
    status: 'hot',
    score: 82,
    bot: 'Bot Email Funnel',
    campaign: 'Campaña Email Bienvenida',
    lastActivity: 'hace 1 hora',
    joinedDate: '17/07/2026',
    funnelStep: 4,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Preguntó por dossier de Japón. Muy interesada.',
  },
  {
    id: 'lead-010',
    name: 'Roberto Jiménez',
    contact: '+34 633 210 987',
    source: 'whatsapp',
    status: 'contacted',
    score: 38,
    bot: 'Bot WhatsApp Outreach',
    campaign: 'WhatsApp Dossier Viaje — Aportación €0.50',
    lastActivity: 'hace 6 horas',
    joinedDate: '17/07/2026',
    funnelStep: 2,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Respondió al primer mensaje. Pidiendo más información.',
  },
  {
    id: 'lead-011',
    name: 'Carmen Díaz',
    contact: 'carmen.diaz@yahoo.es',
    source: 'organic',
    status: 'buyer',
    score: 88,
    bot: 'Bot Retargeting IA',
    campaign: 'Retargeting Compradores IA',
    lastActivity: 'hace 5 horas',
    joinedDate: '16/07/2026',
    funnelStep: 5,
    hasPurchased: true,
    dossierCount: 1,
    notes: 'Primera aportación: dossier Lisboa. Potencial colaborador recurrente.',
  },
  {
    id: 'lead-012',
    name: 'Alejandro Vega',
    contact: 'a.vega@empresa.com',
    phone: '+34 611 876 543',
    source: 'email',
    status: 'cold',
    score: 15,
    bot: 'Bot Email Funnel',
    campaign: 'Campaña Email Bienvenida',
    lastActivity: 'hace 5 días',
    joinedDate: '15/07/2026',
    funnelStep: 1,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'No ha abierto ningún email. Considerar eliminar de la lista.',
  },
  {
    id: 'lead-013',
    name: 'Isabel Romero',
    contact: '+34 666 543 210',
    source: 'whatsapp',
    status: 'hot',
    score: 76,
    bot: 'Bot WhatsApp Outreach',
    campaign: 'WhatsApp Dossier Viaje €0.50',
    lastActivity: 'hace 30 min',
    joinedDate: '20/07/2026',
    funnelStep: 3,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Activa en WhatsApp. Preguntó por aportación para dossier Maldivas.',
  },
  {
    id: 'lead-014',
    name: 'Francisco Navarro',
    contact: 'fran.navarro@gmail.com',
    source: 'organic',
    status: 'warm',
    score: 61,
    bot: 'Bot Retargeting IA',
    campaign: 'Retargeting Compradores IA',
    lastActivity: 'hace 2 días',
    joinedDate: '16/07/2026',
    funnelStep: 3,
    hasPurchased: false,
    dossierCount: 0,
    notes: 'Comparó dossiers de vuelos. Interés en colaborar con dossier completo pendiente.',
  },
  {
    id: 'lead-015',
    name: 'Lucía Castillo',
    contact: '+34 699 876 543',
    phone: '+34 699 876 543',
    source: 'whatsapp',
    status: 'buyer',
    score: 93,
    bot: 'Bot WhatsApp Outreach',
    campaign: 'WhatsApp Dossier Viaje — Aportación €0.50',
    lastActivity: 'hace 3 horas',
    joinedDate: '15/07/2026',
    funnelStep: 5,
    hasPurchased: true,
    dossierCount: 4,
    notes: 'Top colaboradora. 4 dossiers. Muy satisfecha con el servicio.',
  },
];

type FilterTab = 'all' | 'hot' | 'warm' | 'cold' | 'buyer';
type SortField = 'name' | 'score' | 'lastActivity' | 'funnelStep';
type SortDir = 'asc' | 'desc';

const sourceConfig = {
  email: { icon: Mail, label: 'Email', color: 'text-primary' },
  whatsapp: { icon: MessageCircle, label: 'WhatsApp', color: 'text-accent' },
  organic: { icon: Globe, label: 'Orgánico', color: 'text-muted-foreground' },
};

const filterTabs: Array<{ key: FilterTab; label: string; count: number }> = [
  { key: 'all', label: 'Todos', count: allLeads.length },
  {
    key: 'hot',
    label: 'Calientes',
    count: allLeads.filter((l) => l.status === 'hot').length,
  },
  {
    key: 'warm',
    label: 'Tibios',
    count: allLeads.filter((l) => l.status === 'warm').length,
  },
  {
    key: 'cold',
    label: 'Fríos',
    count: allLeads.filter((l) => l.status === 'cold').length,
  },
  {
    key: 'buyer',
    label: 'Colaboradores',
    count: allLeads.filter((l) => l.status === 'buyer').length,
  },
];

export default function LeadManagementClient() {
  const [activeTab, setActiveTab] = useState<FilterTab>('all');
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState<SortField>('score');
  const [sortDir, setSortDir] = useState<SortDir>('desc');
  const [selectedLead, setSelectedLead] = useState<Lead | null>(null);
  const [page, setPage] = useState(1);
  const pageSize = 10;

  const filteredLeads = useMemo(() => {
    let leads = allLeads;

    if (activeTab !== 'all') {
      leads = leads.filter((l) => l.status === activeTab);
    }

    if (search.trim()) {
      const q = search.toLowerCase();
      leads = leads.filter(
        (l) =>
          l.name.toLowerCase().includes(q) ||
          l.contact.toLowerCase().includes(q) ||
          l.campaign.toLowerCase().includes(q)
      );
    }

    leads = [...leads].sort((a, b) => {
      let av: string | number = a[sortField] as string | number;
      let bv: string | number = b[sortField] as string | number;
      if (typeof av === 'string' && typeof bv === 'string') {
        return sortDir === 'asc'
          ? av.localeCompare(bv)
          : bv.localeCompare(av);
      }
      return sortDir === 'asc'
        ? (av as number) - (bv as number)
        : (bv as number) - (av as number);
    });

    return leads;
  }, [activeTab, search, sortField, sortDir]);

  const totalPages = Math.ceil(filteredLeads.length / pageSize);
  const paginatedLeads = filteredLeads.slice(
    (page - 1) * pageSize,
    page * pageSize
  );

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDir('desc');
    }
    setPage(1);
  };

  const handleExport = () => {
    toast.success(`Exportando ${filteredLeads.length} leads a CSV...`);
  };

  const SortIcon = ({ field }: { field: SortField }) => {
    if (sortField !== field)
      return <ChevronDown size={10} className="text-muted-foreground/40" />;
    return sortDir === 'asc' ? (
      <ChevronUp size={10} className="text-primary" />
    ) : (
      <ChevronDown size={10} className="text-primary" />
    );
  };

  return (
    <div className="flex gap-6">
      {/* Main content */}
      <div className="flex-1 min-w-0 space-y-4">
        {/* Toolbar */}
        <div className="flex items-center gap-3 flex-wrap">
          {/* Filter tabs */}
          <div className="flex items-center gap-1 bg-card border border-border rounded-lg p-1">
            {filterTabs.map((tab) => (
              <button
                key={`tab-${tab.key}`}
                onClick={() => {
                  setActiveTab(tab.key);
                  setPage(1);
                }}
                className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-all duration-150 ${
                  activeTab === tab.key
                    ? 'bg-primary text-primary-foreground'
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                }`}
              >
                {tab.label}
                <span
                  className={`text-2xs px-1.5 py-0.5 rounded-full font-semibold ${
                    activeTab === tab.key
                      ? 'bg-white/20 text-white' :'bg-secondary text-muted-foreground'
                  }`}
                >
                  {tab.count}
                </span>
              </button>
            ))}
          </div>

          {/* Search */}
          <div className="relative flex-1 min-w-[200px] max-w-xs">
            <Search
              size={14}
              className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground"
            />
            <input
              type="text"
              placeholder="Buscar lead, email, campaña..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              className="w-full bg-card border border-border rounded-lg pl-9 pr-3 py-2 text-xs text-foreground placeholder:text-muted-foreground focus:outline-none focus:ring-1 focus:ring-primary transition-all"
            />
          </div>

          <div className="flex items-center gap-2 ml-auto">
            <button className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:border-primary transition-all duration-150">
              <Filter size={12} />
              Filtros
            </button>
            <button
              onClick={handleExport}
              className="flex items-center gap-1.5 px-3 py-2 rounded-lg border border-border text-xs text-muted-foreground hover:text-foreground hover:border-accent transition-all duration-150"
            >
              <Download size={12} />
              Exportar CSV
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="bg-card border border-border rounded-xl overflow-hidden">
          <div className="overflow-x-auto scrollbar-thin">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  {[
                    { key: 'col-name', label: 'Nombre', sortKey: 'name' as SortField },
                    { key: 'col-contact', label: 'Contacto', sortKey: null },
                    { key: 'col-source', label: 'Fuente', sortKey: null },
                    { key: 'col-score', label: 'Score', sortKey: 'score' as SortField },
                    { key: 'col-status', label: 'Estado', sortKey: null },
                    { key: 'col-bot', label: 'Bot', sortKey: null },
                    { key: 'col-funnel', label: 'Paso', sortKey: 'funnelStep' as SortField },
                    { key: 'col-activity', label: 'Última Actividad', sortKey: 'lastActivity' as SortField },
                    { key: 'col-purchase', label: 'Dossiers', sortKey: null },
                  ].map((col) => (
                    <th
                      key={col.key}
                      className="px-4 py-3 text-left text-2xs font-medium text-muted-foreground uppercase tracking-wide whitespace-nowrap"
                    >
                      {col.sortKey ? (
                        <button
                          onClick={() => handleSort(col.sortKey!)}
                          className="flex items-center gap-1 hover:text-foreground transition-colors"
                        >
                          {col.label}
                          <SortIcon field={col.sortKey} />
                        </button>
                      ) : (
                        col.label
                      )}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {paginatedLeads.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-4 py-12 text-center">
                      <p className="text-sm text-muted-foreground">
                        No se encontraron leads con estos filtros
                      </p>
                    </td>
                  </tr>
                ) : (
                  paginatedLeads.map((lead, index) => {
                    const src = sourceConfig[lead.source];
                    const SrcIcon = src.icon;
                    const isSelected = selectedLead?.id === lead.id;

                    return (
                      <tr
                        key={lead.id}
                        onClick={() =>
                          setSelectedLead(isSelected ? null : lead)
                        }
                        className={`border-b border-border/50 cursor-pointer transition-colors duration-100 ${
                          isSelected
                            ? 'bg-primary/10'
                            : index % 2 === 0
                            ? 'hover:bg-secondary/50' :'bg-secondary/20 hover:bg-secondary/50'
                        }`}
                      >
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-7 h-7 rounded-full bg-secondary flex items-center justify-center text-xs font-semibold text-muted-foreground shrink-0">
                              {lead.name.charAt(0)}
                            </div>
                            <span className="text-xs font-medium text-foreground whitespace-nowrap">
                              {lead.name}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground font-tabular whitespace-nowrap">
                          {lead.contact}
                        </td>
                        <td className="px-4 py-3">
                          <span
                            className={`flex items-center gap-1.5 text-xs ${src.color} whitespace-nowrap`}
                          >
                            <SrcIcon size={12} />
                            {src.label}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-14 h-1.5 bg-secondary rounded-full overflow-hidden shrink-0">
                              <div
                                className={`h-full rounded-full lead-score-bar ${
                                  lead.score >= 75
                                    ? 'bg-danger'
                                    : lead.score >= 50
                                    ? 'bg-warning' :'bg-muted-foreground'
                                }`}
                                style={{ width: `${lead.score}%` }}
                              />
                            </div>
                            <span className="text-xs font-semibold font-tabular text-foreground">
                              {lead.score}
                            </span>
                          </div>
                        </td>
                        <td className="px-4 py-3">
                          <StatusBadge variant={lead.status} />
                        </td>
                        <td className="px-4 py-3">
                          <span className="flex items-center gap-1 text-2xs text-muted-foreground whitespace-nowrap">
                            <Bot size={10} />
                            {lead.bot.replace('Bot ', '')}
                          </span>
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1.5">
                            {Array.from({ length: 5 }).map((_, i) => (
                              <div
                                key={`step-${lead.id}-${i + 1}`}
                                className={`w-3 h-3 rounded-sm ${
                                  i < lead.funnelStep
                                    ? 'bg-primary' :'bg-secondary'
                                }`}
                              />
                            ))}
                          </div>
                        </td>
                        <td className="px-4 py-3 text-xs text-muted-foreground whitespace-nowrap">
                          {lead.lastActivity}
                        </td>
                        <td className="px-4 py-3">
                          {lead.hasPurchased ? (
                            <span className="flex items-center gap-1 text-xs text-accent font-semibold">
                              <ShoppingBag size={11} />
                              {lead.dossierCount}
                            </span>
                          ) : (
                            <span className="text-xs text-muted-foreground/50">—</span>
                          )}
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between px-4 py-3 border-t border-border">
            <p className="text-xs text-muted-foreground">
              Mostrando {Math.min((page - 1) * pageSize + 1, filteredLeads.length)}–
              {Math.min(page * pageSize, filteredLeads.length)} de{' '}
              {filteredLeads.length} leads
            </p>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                className="px-2 py-1 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                ‹ Anterior
              </button>
              {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                <button
                  key={`page-${p}`}
                  onClick={() => setPage(p)}
                  className={`w-7 h-7 rounded text-xs font-medium transition-all ${
                    page === p
                      ? 'bg-primary text-primary-foreground'
                      : 'text-muted-foreground hover:text-foreground hover:bg-secondary'
                  }`}
                >
                  {p}
                </button>
              ))}
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                className="px-2 py-1 rounded text-xs text-muted-foreground hover:text-foreground hover:bg-secondary disabled:opacity-40 disabled:cursor-not-allowed transition-all"
              >
                Siguiente ›
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Lead Detail Panel */}
      {selectedLead && (
        <div className="w-80 shrink-0 slide-in-right">
          <LeadDetailPanel
            lead={selectedLead}
            onClose={() => setSelectedLead(null)}
          />
        </div>
      )}
    </div>
  );
}

function LeadDetailPanel({
  lead,
  onClose,
}: {
  lead: Lead;
  onClose: () => void;
}) {
  const src = sourceConfig[lead.source];
  const SrcIcon = src.icon;

  const handleAction = (action: string) => {
    toast.success(`${action} enviado a ${lead.name}`);
  };

  const timeline = [
    { id: `tl-${lead.id}-1`, event: 'Lead captado', time: lead.joinedDate, icon: Star },
    { id: `tl-${lead.id}-2`, event: 'Primer email enviado', time: 'hace 2 días', icon: Mail },
    { id: `tl-${lead.id}-3`, event: 'Email abierto', time: 'hace 1 día', icon: Mail },
    ...(lead.hasPurchased
      ? [{ id: `tl-${lead.id}-4`, event: `Aportó para ${lead.dossierCount} dossier(s)`, time: lead.lastActivity, icon: ShoppingBag }]
      : [{ id: `tl-${lead.id}-4`, event: 'Última actividad registrada', time: lead.lastActivity, icon: Clock }]),
  ];

  return (
    <div className="bg-card border border-border rounded-xl overflow-hidden sticky top-6">
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-4 border-b border-border">
        <div className="flex items-center gap-2.5">
          <div className="w-9 h-9 rounded-full bg-primary/15 flex items-center justify-center text-sm font-bold text-primary">
            {lead.name.charAt(0)}
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground">{lead.name}</p>
            <StatusBadge variant={lead.status} />
          </div>
        </div>
        <button
          onClick={onClose}
          className="p-1.5 rounded-lg text-muted-foreground hover:text-foreground hover:bg-secondary transition-all"
        >
          <X size={14} />
        </button>
      </div>

      <div className="p-4 space-y-4 overflow-y-auto scrollbar-thin max-h-[calc(100vh-200px)]">
        {/* Score */}
        <div>
          <div className="flex justify-between text-xs mb-1.5">
            <span className="text-muted-foreground font-medium">Lead Score</span>
            <span
              className={`font-bold font-tabular ${
                lead.score >= 75
                  ? 'text-danger'
                  : lead.score >= 50
                  ? 'text-warning' :'text-muted-foreground'
              }`}
            >
              {lead.score}/100
            </span>
          </div>
          <div className="w-full h-2 bg-secondary rounded-full overflow-hidden">
            <div
              className={`h-full rounded-full lead-score-bar ${
                lead.score >= 75
                  ? 'bg-danger'
                  : lead.score >= 50
                  ? 'bg-warning' :'bg-muted-foreground'
              }`}
              style={{ width: `${lead.score}%` }}
            />
          </div>
        </div>

        {/* Contact info */}
        <div className="space-y-2">
          <p className="text-2xs font-medium text-muted-foreground uppercase tracking-wide">
            Contacto
          </p>
          <div className="space-y-1.5">
            <div className="flex items-center gap-2 text-xs">
              <SrcIcon size={12} className={src.color} />
              <span className="text-foreground font-tabular">{lead.contact}</span>
            </div>
            {lead.phone && lead.phone !== lead.contact && (
              <div className="flex items-center gap-2 text-xs">
                <Phone size={12} className="text-muted-foreground" />
                <span className="text-foreground font-tabular">{lead.phone}</span>
              </div>
            )}
          </div>
        </div>

        {/* Campaign & Bot */}
        <div className="space-y-2">
          <p className="text-2xs font-medium text-muted-foreground uppercase tracking-wide">
            Campaña & Bot
          </p>
          <div className="bg-secondary rounded-lg p-3 space-y-1.5">
            <div className="flex items-center gap-2 text-xs">
              <Bot size={11} className="text-primary" />
              <span className="text-foreground">{lead.bot}</span>
            </div>
            <div className="text-2xs text-muted-foreground">{lead.campaign}</div>
          </div>
        </div>

        {/* Funnel position */}
        <div className="space-y-2">
          <p className="text-2xs font-medium text-muted-foreground uppercase tracking-wide">
            Posición en Embudo
          </p>
          <div className="flex items-center gap-1.5">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={`detail-step-${lead.id}-${i + 1}`} className="flex-1">
                <div
                  className={`h-2 rounded-sm ${
                    i < lead.funnelStep ? 'bg-primary' : 'bg-secondary'
                  }`}
                />
                <p className="text-2xs text-muted-foreground text-center mt-0.5">
                  {i + 1}
                </p>
              </div>
            ))}
          </div>
          <p className="text-2xs text-muted-foreground">
            Paso {lead.funnelStep} de 5 —{' '}
            {lead.funnelStep === 1
              ? 'Captación'
              : lead.funnelStep === 2
              ? 'Lead Magnet'
              : lead.funnelStep === 3
              ? 'Secuencia'
              : lead.funnelStep === 4
              ? 'Oferta — Aportación €0.50' :'Upsell'}
          </p>
        </div>

        {/* Purchase info */}
        {lead.hasPurchased && (
          <div className="bg-accent/10 border border-accent/20 rounded-lg p-3">
            <div className="flex items-center gap-2 mb-1">
              <ShoppingBag size={12} className="text-accent" />
              <p className="text-xs font-semibold text-accent">Colaborador</p>
            </div>
            <p className="text-2xs text-muted-foreground">
              {lead.dossierCount} dossier{lead.dossierCount > 1 ? 's' : ''} solicitado
              {lead.dossierCount > 1 ? 's' : ''} ·{' '}
              <span className="font-semibold text-warning font-tabular">
                €{(lead.dossierCount * 0.5).toFixed(2)}
              </span>{' '}
              en aportaciones
            </p>
          </div>
        )}

        {/* Notes */}
        <div className="space-y-2">
          <p className="text-2xs font-medium text-muted-foreground uppercase tracking-wide">
            Notas del Bot
          </p>
          <p className="text-xs text-muted-foreground leading-relaxed bg-secondary rounded-lg p-3">
            {lead.notes}
          </p>
        </div>

        {/* Activity timeline */}
        <div className="space-y-2">
          <p className="text-2xs font-medium text-muted-foreground uppercase tracking-wide">
            Actividad Reciente
          </p>
          <div className="space-y-2">
            {timeline.map((item, idx) => {
              const TIcon = item.icon;
              return (
                <div key={item.id} className="flex items-start gap-2.5">
                  <div className="flex flex-col items-center">
                    <span className="p-1 rounded-full bg-secondary shrink-0">
                      <TIcon size={9} className="text-muted-foreground" />
                    </span>
                    {idx < timeline.length - 1 && (
                      <div className="w-px h-4 bg-border mt-0.5" />
                    )}
                  </div>
                  <div className="min-w-0 pb-1">
                    <p className="text-xs text-foreground leading-snug">{item.event}</p>
                    <p className="text-2xs text-muted-foreground">{item.time}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Quick actions */}
        <div className="space-y-2 pt-2 border-t border-border">
          <p className="text-2xs font-medium text-muted-foreground uppercase tracking-wide">
            Acciones Rápidas
          </p>
          <div className="grid grid-cols-2 gap-2">
            <button
              onClick={() => handleAction('Email')}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-primary/10 text-primary border border-primary/20 text-xs font-medium hover:bg-primary/20 active:scale-95 transition-all duration-150"
            >
              <Mail size={12} />
              Enviar Email
            </button>
            <button
              onClick={() => handleAction('WhatsApp')}
              className="flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-accent/10 text-accent border border-accent/20 text-xs font-medium hover:bg-accent/20 active:scale-95 transition-all duration-150"
            >
              <MessageCircle size={12} />
              WhatsApp
            </button>
            <button
              onClick={() => handleAction('Marcado como colaborador')}
              className="col-span-2 flex items-center justify-center gap-1.5 px-3 py-2 rounded-lg bg-warning/10 text-warning border border-warning/20 text-xs font-medium hover:bg-warning/20 active:scale-95 transition-all duration-150"
            >
              <ShoppingBag size={12} />
              Marcar como Colaborador
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}